/**
 * GoldenSetManager — CRUD + generadores de golden set para QA del Catalogador IA.
 *
 * Endpoints:
 *   GET    /api/qa/golden?source=manual|batch_derived|random
 *   POST   /api/qa/golden
 *   PATCH  /api/qa/golden/:id
 *   DELETE /api/qa/golden/:id
 *   POST   /api/qa/golden/generate-random        body {count}
 *   POST   /api/qa/golden/generate-from-batch    body {count, intake?, sitio?, minScore}
 *
 * UI:
 *   - Tabla con paginacion cliente (50/pagina).
 *   - Filtros: source (select), search por product_name.
 *   - Botones: "+ Nuevo manual", "+ Generar Random", "+ Generar desde Batch".
 *   - Editar / eliminar por fila.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE } from '../config';
import './GoldenSetManager.css';

const PAGE_SIZE = 50;
const SOURCES = [
  { value: '',              label: 'Todos los origenes' },
  { value: 'manual',        label: 'Manual' },
  { value: 'batch_derived', label: 'Desde batch' },
  { value: 'random',        label: 'Random' },
];

function fmtDateTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch (_) {
    return '—';
  }
}

function SourceBadge({ source }) {
  const cls = source === 'manual'        ? 'qa-src-manual'
            : source === 'batch_derived' ? 'qa-src-batch'
            : source === 'random'        ? 'qa-src-random'
            : 'qa-src-other';
  const label = source === 'batch_derived' ? 'batch' : (source || '—');
  return <span className={`qa-src-pill ${cls}`}>{label}</span>;
}

function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  return (
    <div className={`um-toast ${toast.type === 'error' ? 'um-toast-error' : ''}`}>
      {toast.message}
    </div>
  );
}

function ItemModal({ mode, initial, busy, onClose, onSubmit }) {
  const [productName,  setProductName]  = useState(initial?.product_name || '');
  const [idMaestro,    setIdMaestro]    = useState(initial?.id_maestro || '');
  const [expectedId,   setExpectedId]   = useState(initial?.expected_match_id_catalogo || '');
  const [expectedSitio,setExpectedSitio]= useState(initial?.expected_sitio || '');
  const [expectedMin,  setExpectedMin]  = useState(initial?.expected_score_min ?? '');
  const [notes,        setNotes]        = useState(initial?.notes || '');
  const [isActive,     setIsActive]     = useState(initial?.is_active !== false);
  const [formError,    setFormError]    = useState(null);

  const isEdit = mode === 'edit';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!productName.trim()) {
      setFormError('product_name es requerido.');
      return;
    }
    const payload = {
      product_name: productName.trim(),
      id_maestro: idMaestro.trim() || null,
      expected_match_id_catalogo: expectedId.trim() || null,
      expected_sitio: expectedSitio.trim() || null,
      expected_score_min: expectedMin === '' ? null : Number(expectedMin),
      notes: notes.trim() || null,
    };
    if (isEdit) payload.is_active = isActive;
    try {
      await onSubmit(payload);
    } catch (err) {
      setFormError(err.message || 'Error al guardar.');
    }
  };

  return (
    <div className="um-modal-overlay" onClick={onClose}>
      <div className="um-modal qa-modal-md" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="um-modal-header">
            <h3>{isEdit ? 'Editar item' : 'Nuevo item manual'}</h3>
          </div>
          <div className="um-modal-body">
            {formError && <div className="um-error">{formError}</div>}
            <div className="um-field">
              <label>Product name</label>
              <input
                type="text"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                placeholder="Nombre del producto"
                required
              />
            </div>
            <div className="qa-form-row">
              <div className="um-field">
                <label>id_maestro (opcional)</label>
                <input
                  type="text"
                  value={idMaestro}
                  onChange={(e) => setIdMaestro(e.target.value)}
                />
              </div>
              <div className="um-field">
                <label>expected_match_id_catalogo</label>
                <input
                  type="text"
                  value={expectedId}
                  onChange={(e) => setExpectedId(e.target.value)}
                />
              </div>
            </div>
            <div className="qa-form-row">
              <div className="um-field">
                <label>expected_sitio</label>
                <input
                  type="text"
                  value={expectedSitio}
                  onChange={(e) => setExpectedSitio(e.target.value)}
                  placeholder="tipti, frecuento, tuti..."
                />
              </div>
              <div className="um-field">
                <label>expected_score_min</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={expectedMin}
                  onChange={(e) => setExpectedMin(e.target.value)}
                />
              </div>
            </div>
            <div className="um-field">
              <label>Notas</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
            {isEdit && (
              <div className="um-field um-checkbox-row">
                <input
                  id="qa-gs-active"
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                <label htmlFor="qa-gs-active">Item activo</label>
              </div>
            )}
          </div>
          <div className="um-modal-footer">
            <button type="button" className="um-btn um-btn-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
            <button type="submit" className="um-btn um-btn-primary" disabled={busy}>
              {busy ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function GenerateRandomModal({ busy, onClose, onSubmit }) {
  const [count, setCount] = useState(20);
  const [formError, setFormError] = useState(null);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    const n = Number(count);
    if (!Number.isFinite(n) || n <= 0) {
      setFormError('count debe ser un entero positivo.');
      return;
    }
    try { await onSubmit({ count: n }); }
    catch (err) { setFormError(err.message || 'Error.'); }
  };
  return (
    <div className="um-modal-overlay" onClick={onClose}>
      <div className="um-modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="um-modal-header"><h3>Generar random</h3></div>
          <div className="um-modal-body">
            {formError && <div className="um-error">{formError}</div>}
            <p>Generar N items random sampleando el maestro.</p>
            <div className="um-field">
              <label>Cantidad</label>
              <input
                type="number"
                min="1"
                max="500"
                value={count}
                onChange={(e) => setCount(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="um-modal-footer">
            <button type="button" className="um-btn um-btn-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
            <button type="submit" className="um-btn um-btn-primary" disabled={busy}>
              {busy ? 'Generando...' : 'Generar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function GenerateFromBatchModal({ busy, onClose, onSubmit }) {
  const [count,    setCount]    = useState(20);
  const [intake,   setIntake]   = useState('');
  const [sitio,    setSitio]    = useState('');
  const [minScore, setMinScore] = useState(80);
  const [formError, setFormError] = useState(null);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    const n = Number(count);
    const ms = Number(minScore);
    if (!Number.isFinite(n) || n <= 0) {
      setFormError('count debe ser un entero positivo.');
      return;
    }
    try {
      await onSubmit({
        count: n,
        intake: intake.trim() || undefined,
        sitio: sitio.trim() || undefined,
        minScore: Number.isFinite(ms) ? ms : 80,
      });
    } catch (err) {
      setFormError(err.message || 'Error.');
    }
  };
  return (
    <div className="um-modal-overlay" onClick={onClose}>
      <div className="um-modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="um-modal-header"><h3>Generar desde batch</h3></div>
          <div className="um-modal-body">
            {formError && <div className="um-error">{formError}</div>}
            <p>Derivar items de matches ya existentes en la BD (score &gt;= minScore).</p>
            <div className="qa-form-row">
              <div className="um-field">
                <label>Cantidad</label>
                <input
                  type="number"
                  min="1"
                  max="500"
                  value={count}
                  onChange={(e) => setCount(e.target.value)}
                  required
                />
              </div>
              <div className="um-field">
                <label>minScore</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={minScore}
                  onChange={(e) => setMinScore(e.target.value)}
                />
              </div>
            </div>
            <div className="qa-form-row">
              <div className="um-field">
                <label>Intake (opcional)</label>
                <input
                  type="text"
                  value={intake}
                  onChange={(e) => setIntake(e.target.value)}
                  placeholder="ej: 2026-05-30"
                />
              </div>
              <div className="um-field">
                <label>Sitio (opcional)</label>
                <input
                  type="text"
                  value={sitio}
                  onChange={(e) => setSitio(e.target.value)}
                  placeholder="tipti / frecuento / tuti"
                />
              </div>
            </div>
          </div>
          <div className="um-modal-footer">
            <button type="button" className="um-btn um-btn-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
            <button type="submit" className="um-btn um-btn-primary" disabled={busy}>
              {busy ? 'Generando...' : 'Generar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteConfirm({ item, busy, onClose, onConfirm }) {
  return (
    <div className="um-modal-overlay" onClick={onClose}>
      <div className="um-modal" onClick={(e) => e.stopPropagation()}>
        <div className="um-modal-header"><h3>Eliminar item</h3></div>
        <div className="um-modal-body">
          <p>Vas a eliminar <strong>{item?.product_name}</strong>. Esta accion no se puede deshacer.</p>
        </div>
        <div className="um-modal-footer">
          <button className="um-btn um-btn-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
          <button className="um-btn um-btn-danger" onClick={onConfirm} disabled={busy}>
            {busy ? 'Eliminando...' : 'Eliminar'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function GoldenSetManager() {
  const [items,   setItems]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [source,  setSource]  = useState('');
  const [search,  setSearch]  = useState('');
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [toast,   setToast]   = useState(null);

  const [itemModal,    setItemModal]    = useState({ mode: null, item: null, busy: false });
  const [randomModal,  setRandomModal]  = useState({ open: false, busy: false });
  const [batchModal,   setBatchModal]   = useState({ open: false, busy: false });
  const [deleteState,  setDeleteState]  = useState({ item: null, busy: false });

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = source
        ? `${API_BASE}/api/qa/golden?source=${encodeURIComponent(source)}`
        : `${API_BASE}/api/qa/golden`;
      const res = await fetch(url);
      if (res.status === 401 || res.status === 403) {
        throw new Error('Sin permisos para ver golden set (se requiere superadmin).');
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data.data) ? data.data : [];
      setItems(list);
      setTotal(Number.isFinite(data.total) ? data.total : list.length);
    } catch (err) {
      console.error('[GoldenSetManager] fetch error:', err);
      setError(err.message || 'Error al cargar golden set');
    } finally {
      setLoading(false);
    }
  }, [source]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { setPage(1); }, [source, search]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(i =>
      (i.product_name && i.product_name.toLowerCase().includes(q)) ||
      (i.expected_match_id_catalogo && String(i.expected_match_id_catalogo).toLowerCase().includes(q)) ||
      (i.expected_sitio && i.expected_sitio.toLowerCase().includes(q))
    );
  }, [items, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, safePage]);

  const handleCreate = async (payload) => {
    setItemModal(s => ({ ...s, busy: true }));
    try {
      const res = await fetch(`${API_BASE}/api/qa/golden`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 401 || res.status === 403) throw new Error('Sin permisos.');
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      setItemModal({ mode: null, item: null, busy: false });
      showToast('Item creado');
      await fetchItems();
    } catch (err) {
      setItemModal(s => ({ ...s, busy: false }));
      throw err;
    }
  };

  const handleUpdate = async (payload) => {
    const item = itemModal.item;
    if (!item) return;
    setItemModal(s => ({ ...s, busy: true }));
    try {
      const res = await fetch(`${API_BASE}/api/qa/golden/${encodeURIComponent(item.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 401 || res.status === 403) throw new Error('Sin permisos.');
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      setItemModal({ mode: null, item: null, busy: false });
      showToast('Item actualizado');
      await fetchItems();
    } catch (err) {
      setItemModal(s => ({ ...s, busy: false }));
      throw err;
    }
  };

  const handleDelete = async () => {
    const item = deleteState.item;
    if (!item) return;
    setDeleteState(s => ({ ...s, busy: true }));
    try {
      const res = await fetch(`${API_BASE}/api/qa/golden/${encodeURIComponent(item.id)}`, {
        method: 'DELETE',
      });
      if (res.status === 401 || res.status === 403) {
        showToast('Sin permisos.', 'error');
      } else if (!res.ok) {
        const j = await res.json().catch(() => null);
        showToast(j?.error || `HTTP ${res.status}`, 'error');
      } else {
        showToast('Item eliminado');
        await fetchItems();
      }
    } catch (err) {
      showToast(err.message || 'Error al eliminar', 'error');
    } finally {
      setDeleteState({ item: null, busy: false });
    }
  };

  const handleGenerateRandom = async (payload) => {
    setRandomModal(s => ({ ...s, busy: true }));
    try {
      const res = await fetch(`${API_BASE}/api/qa/golden/generate-random`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 401 || res.status === 403) throw new Error('Sin permisos.');
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      const data = await res.json().catch(() => ({}));
      const inserted = data?.inserted ?? data?.count ?? payload.count;
      setRandomModal({ open: false, busy: false });
      showToast(`${inserted} items generados (random)`);
      await fetchItems();
    } catch (err) {
      setRandomModal(s => ({ ...s, busy: false }));
      throw err;
    }
  };

  const handleGenerateFromBatch = async (payload) => {
    setBatchModal(s => ({ ...s, busy: true }));
    try {
      const res = await fetch(`${API_BASE}/api/qa/golden/generate-from-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 401 || res.status === 403) throw new Error('Sin permisos.');
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      const data = await res.json().catch(() => ({}));
      const inserted = data?.inserted ?? data?.count ?? payload.count;
      setBatchModal({ open: false, busy: false });
      showToast(`${inserted} items generados (desde batch)`);
      await fetchItems();
    } catch (err) {
      setBatchModal(s => ({ ...s, busy: false }));
      throw err;
    }
  };

  return (
    <>
      <div className="um-card">
        <div className="um-toolbar">
          <div className="um-toolbar-left">
            <select
              className="qa-select"
              value={source}
              onChange={(e) => setSource(e.target.value)}
            >
              {SOURCES.map(s => <option key={s.value || 'all'} value={s.value}>{s.label}</option>)}
            </select>
            <input
              className="um-search"
              type="search"
              placeholder="Buscar por nombre / id / sitio..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <span className="um-count">
              {loading ? '...' : `${filtered.length} de ${total}`}
            </span>
          </div>
          <div className="qa-toolbar-actions">
            <button
              className="um-btn um-btn-ghost"
              onClick={fetchItems}
              disabled={loading}
            >
              Refrescar
            </button>
            <button
              className="um-btn um-btn-ghost"
              onClick={() => setRandomModal({ open: true, busy: false })}
            >
              + Random
            </button>
            <button
              className="um-btn um-btn-ghost"
              onClick={() => setBatchModal({ open: true, busy: false })}
            >
              + Desde batch
            </button>
            <button
              className="um-btn um-btn-primary"
              onClick={() => setItemModal({ mode: 'create', item: null, busy: false })}
            >
              + Nuevo manual
            </button>
          </div>
        </div>

        {error && <div className="um-error" style={{ margin: 14 }}>{error}</div>}

        <div className="um-table-wrap">
          {loading ? (
            <div className="um-loading">Cargando golden set...</div>
          ) : filtered.length === 0 ? (
            <div className="um-empty">
              {items.length === 0 ? 'No hay items en el golden set.' : 'Sin resultados.'}
            </div>
          ) : (
            <table className="um-table">
              <thead>
                <tr>
                  <th>Product name</th>
                  <th>Expected id_catalogo</th>
                  <th>Sitio</th>
                  <th>Score min</th>
                  <th>Source</th>
                  <th>Activo</th>
                  <th>Creado</th>
                  <th style={{ textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(it => (
                  <tr key={it.id}>
                    <td className="qa-cell-name" title={it.product_name}>{it.product_name}</td>
                    <td><code className="qa-mono">{it.expected_match_id_catalogo || '—'}</code></td>
                    <td>{it.expected_sitio || '—'}</td>
                    <td>{it.expected_score_min != null ? Number(it.expected_score_min).toFixed(2) : '—'}</td>
                    <td><SourceBadge source={it.source} /></td>
                    <td>
                      {it.is_active === false
                        ? <span className="um-active-no">No</span>
                        : <span className="um-active-yes">Si</span>}
                    </td>
                    <td>{fmtDateTime(it.created_at)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="um-btn um-btn-link"
                        onClick={() => setItemModal({ mode: 'edit', item: it, busy: false })}
                      >
                        Editar
                      </button>
                      <button
                        className="um-btn um-btn-link-danger"
                        onClick={() => setDeleteState({ item: it, busy: false })}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {filtered.length > PAGE_SIZE && (
          <div className="qa-pagination">
            <button
              className="um-btn um-btn-ghost"
              disabled={safePage <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              ← Anterior
            </button>
            <span className="qa-pagination-info">
              Pagina {safePage} de {totalPages}
            </span>
            <button
              className="um-btn um-btn-ghost"
              disabled={safePage >= totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            >
              Siguiente →
            </button>
          </div>
        )}
      </div>

      {itemModal.mode && (
        <ItemModal
          mode={itemModal.mode}
          initial={itemModal.item}
          busy={itemModal.busy}
          onClose={() => itemModal.busy ? null : setItemModal({ mode: null, item: null, busy: false })}
          onSubmit={itemModal.mode === 'create' ? handleCreate : handleUpdate}
        />
      )}

      {randomModal.open && (
        <GenerateRandomModal
          busy={randomModal.busy}
          onClose={() => randomModal.busy ? null : setRandomModal({ open: false, busy: false })}
          onSubmit={handleGenerateRandom}
        />
      )}

      {batchModal.open && (
        <GenerateFromBatchModal
          busy={batchModal.busy}
          onClose={() => batchModal.busy ? null : setBatchModal({ open: false, busy: false })}
          onSubmit={handleGenerateFromBatch}
        />
      )}

      {deleteState.item && (
        <DeleteConfirm
          item={deleteState.item}
          busy={deleteState.busy}
          onClose={() => deleteState.busy ? null : setDeleteState({ item: null, busy: false })}
          onConfirm={handleDelete}
        />
      )}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
}
